/**
 * Re-terming every offer the tender's term series does not describe, in one action.
 *
 * ⚠️ WHY THIS EXISTS.
 *
 * A tender that asked for 4/16/28/40 months gets answers on 12/24/36 from any retailer that
 * quoted its standard ladder instead. WERRIBEE RACING CLUB had three of them — ALINTA, Engie
 * and Momentum. Their rates are fine; only the LENGTH each rate is billed over is wrong, and
 * the client's own model handles it by keeping the rate and applying the tender's span:
 *
 *     annual cost = ALINTA's 9.52 c/kWh x kWh      <- the retailer's own rate, untouched
 *     term total  = annual cost x 121/365          <- the TENDER's 4 months, not ALINTA's 12
 *
 * Doing that by hand is nine edits per tender (three offers x three terms, each with its own
 * Save), and a missed one fails silently: that offer is priced over 365 days while every other
 * retailer is on 121, so the totals no longer sit on the same contract length and the
 * comparison is meaningless with nothing on screen to say so.
 *
 * ⚠️ THE GUARD: ONE FITTING TERM DISQUALIFIES THE WHOLE OFFER.
 *
 * Positional mapping is only safe when the offer's ladder is uniformly wrong. Flo quotes
 * 16/28/40/52 against this same series — its 16, 28 and 40 ALREADY sit on Years 2, 3 and 4
 * because it skipped the 4-month option. Mapping by position would shove 16 -> 4, 28 -> 16,
 * 40 -> 28 and move three correct terms onto wrong lengths. The client's sheet agrees with
 * leaving it alone: it shows Flo's Year 1 blank.
 *
 * So an offer with even ONE term inside the series is never touched automatically. That single
 * rule is what makes `16 -> 4` impossible, rather than relying on the reviewer to notice.
 */

import {
  normaliseTermSeries, termYearFromSeries, seriesYears, type TenderTermSeries,
} from './tenderTermSeries';
import { rebaseTermYearRanges, getExpectedYearsForTerm } from './termDateRanges';

/** One term key's move: `12_smooth` -> `4_smooth`. */
export type TermMove = {
  fromKey: string;
  toKey: string;
  fromMonths: number;
  toMonths: number;
};

export type OfferRetermPlan = {
  /** Term keys that will be renamed, rates untouched. */
  moves: TermMove[];
  /**
   * Terms with no year left to take, because the offer quotes more lengths than the series has
   * years. Left exactly as they are — dropping a term the retailer quoted is never this
   * function's decision.
   */
  unmapped: number[];
};

/** Why an offer was left alone, so the UI can say so rather than just omitting it. */
export type OfferRetermSkip =
  | { reason: 'fits'; fittingTerms: number[] }
  | { reason: 'no-terms' }
  | { reason: 'no-series' };

const parseTermKey = (key: string): { months: number; suffix: string } | null => {
  const raw = String(key);
  const months = parseInt(raw, 10);
  if (!Number.isFinite(months) || months <= 0) return null;
  const underscore = raw.indexOf('_');
  return { months, suffix: underscore >= 0 ? raw.slice(underscore) : '' };
};

/**
 * What to do with one offer's term keys against the tender's series.
 *
 * Returns a plan only when NOT ONE of the offer's terms fits the series. Distinct month counts
 * are mapped by position — shortest term to Year 1, next to Year 2 — which is what the client's
 * model does and the only ordering that survives an offer quoting fewer terms than the series
 * has years (ALINTA's three terms take Years 1-3 and Year 4 stays blank, exactly as in their
 * sheet).
 *
 * A key's `_smooth` / `_stepped` suffix is preserved, and two keys sharing a month count (an
 * offer quoting both rate types for 12 months) both move to the same new month count.
 */
export const planOfferReterm = (
  termKeys: string[],
  series?: TenderTermSeries | null,
): OfferRetermPlan | OfferRetermSkip => {
  if (normaliseTermSeries(series).size === 0) return { reason: 'no-series' };

  const parsed = (termKeys ?? [])
    .map(k => ({ key: String(k), ...(parseTermKey(String(k)) ?? { months: NaN, suffix: '' }) }))
    .filter(p => Number.isFinite(p.months) && p.months > 0);
  if (parsed.length === 0) return { reason: 'no-terms' };

  const distinctMonths = [...new Set(parsed.map(p => p.months))].sort((a, b) => a - b);

  // THE GUARD. Any term the series already describes means this offer's ladder is not
  // uniformly wrong, and a positional remap would move the terms that are right.
  const fitting = distinctMonths.filter(m => termYearFromSeries(m, series) !== null);
  if (fitting.length > 0) return { reason: 'fits', fittingTerms: fitting };

  const normalised = normaliseTermSeries(series);
  const years = seriesYears(series);

  const moves: TermMove[] = [];
  const unmapped: number[] = [];

  distinctMonths.forEach((months, index) => {
    const year = years[index];
    const target = year !== undefined ? normalised.get(year) : undefined;
    if (target === undefined) { unmapped.push(months); return; }
    if (target === months) return;                       // already correct — nothing to do

    for (const p of parsed.filter(x => x.months === months)) {
      moves.push({ fromKey: p.key, toKey: `${target}${p.suffix}`, fromMonths: months, toMonths: target });
    }
  });

  if (moves.length === 0) return { reason: 'fits', fittingTerms: distinctMonths };
  return { moves, unmapped };
};

/**
 * ⚠️ THE GUARD IS A DEFAULT NOW, NOT A REFUSAL.
 *
 * planOfferReterm above answers one question — "is this offer's ladder uniformly wrong?" — and
 * says nothing at all when it is not. That hid the control entirely: ACR MACKAY asked for
 * 10/22/34/46 and Energy Australia answered 12/24/36/46, where only the 46 is right. Three
 * terms were two months out, the tolerance called them a fit, and the reviewer was given no
 * way to correct them. They re-termed by hand instead.
 *
 * Hiding a control is not the same as not applying it. What the Flo case actually requires is
 * that 16 -> 4 never happens BY ITSELF; the reviewer looking at Flo and deciding otherwise is
 * the whole point of a review screen. So this builds a per-TERM proposal for every offer:
 * a suggested target for each term, pre-selected only where something is genuinely wrong, and
 * every one of them changeable — including to "remove", for the fifth term a retailer sent
 * against a four-year request.
 */

/** Where one term should end up. */
export type TermTarget =
  | { kind: 'year'; year: number; months: number }
  | { kind: 'remove' }
  | { kind: 'keep' };

export type TermProposal = {
  fromKey: string;
  fromMonths: number;
  suffix: string;
  /** What the app suggests. The reviewer may change it to any other target. */
  suggested: TermTarget;
  /** True when the term already sits exactly on the year suggested — nothing to do. */
  alreadyCorrect: boolean;
};

export type OfferRetermProposal = {
  terms: TermProposal[];
  /** Series years this offer already answers EXACTLY, on its own. */
  exactYears: number[];
  /** Whether to pre-tick this offer: true when at least one term would actually move. */
  suggestSelected: boolean;
};

/**
 * A per-term proposal for one offer — the suggestion the panel opens with.
 *
 * Three passes, in this order, so a term that is already right can never be displaced by one
 * that is not:
 *
 *   1. EXACT — a term whose month count equals a series year's takes that year and is marked
 *      alreadyCorrect. Flo's 16, 28 and 40 land on Years 2, 3 and 4 here and stay put.
 *   2. OUTSIDE — a term longer than the series' longest year, or shorter than its shortest, has
 *      no year to take. Suggested "keep", never "remove": dropping a quote the retailer made is
 *      the reviewer's call, and Flo's 52 months is a real offer the client's own sheet simply
 *      leaves off the table. Both ends matter — a 16/28/40/52 tender got Nectr quoting
 *      4/16/28/40, and with only the upper guard the leftover FOUR-month term was proposed as a
 *      fifty-two-month one.
 *   3. THE REST — remaining terms, shortest first, onto remaining years, shortest first. This
 *      is the uniform-ladder case: ALINTA's 12/24/36 onto 4/16/28, and Energy Australia's
 *      12/24/36 onto 10/22/34 with its 46 already parked on Year 4 by pass 1.
 *
 * Anything still unplaced is "keep". Nothing is ever removed by suggestion.
 */
export const proposeOfferReterm = (
  termKeys: string[],
  series?: TenderTermSeries | null,
): OfferRetermProposal => {
  const normalised = normaliseTermSeries(series);
  const parsed = (termKeys ?? [])
    .map(k => ({ key: String(k), ...(parseTermKey(String(k)) ?? { months: NaN, suffix: '' }) }))
    .filter(p => Number.isFinite(p.months) && p.months > 0)
    .sort((a, b) => a.months - b.months);

  const keepAll = (): OfferRetermProposal => ({
    terms: parsed.map(p => ({
      fromKey: p.key, fromMonths: p.months, suffix: p.suffix,
      suggested: { kind: 'keep' } as TermTarget, alreadyCorrect: false,
    })),
    exactYears: [],
    suggestSelected: false,
  });
  if (normalised.size === 0 || parsed.length === 0) return keepAll();

  const years = seriesYears(series);
  const longest = Math.max(...years.map(y => normalised.get(y)!));
  const shortest = Math.min(...years.map(y => normalised.get(y)!));

  /**
   * ⚠️ THE LADDER IS THE MONTH COUNTS, NOT THE KEYS.
   *
   * `_smooth` and `_stepped` are two RATE VARIANTS of one contract length, and Energy Australia
   * quotes both: `12`, `12_stepped`, `24`, `24_stepped`, `36`, `36_stepped`, `46`, `46_stepped`.
   * Deciding per key made that eight separate terms, so 12 claimed Year 1, 12_stepped claimed
   * Year 2, 24 claimed Year 3 — the same twelve-month quote landing on three different years,
   * and 36 with nowhere left to go. The offer would have been shredded rather than re-termed.
   *
   * One decision per month count, applied to every variant of it.
   */
  const distinctMonths = [...new Set(parsed.map(p => p.months))].sort((a, b) => a - b);

  const target = new Map<number, TermTarget>();
  const takenYears = new Set<number>();
  const exactYears: number[] = [];

  // 1 — exact
  for (const months of distinctMonths) {
    const year = years.find(y => normalised.get(y) === months && !takenYears.has(y));
    if (year === undefined) continue;
    target.set(months, { kind: 'year', year, months });
    takenYears.add(year);
    if (!exactYears.includes(year)) exactYears.push(year);
  }

  /**
   * 2 — outside the series at either end: no year exists for it.
   *
   * ⚠️ THE GUARD HAS TO WORK BOTH WAYS, AND IT ONLY EVER LOOKED UP.
   *
   * Longer-than-the-last-year has always been kept — that is what stops Flo's 52-month option
   * landing on a free 4-month Year 1. Shorter-than-the-first-year had no guard at all, so pass 3
   * swept it onto whatever year was still free.
   *
   * A 16 / 28 / 40 / 52 tender got Nectr quoting 4 / 16 / 28 / 40. The 16, 28 and 40 matched
   * exactly, leaving Year 4 free, and the leftover FOUR-month quote was proposed as a
   * FIFTY-TWO-month term — the same disaster as Flo's, arriving from the other direction.
   *
   * A tender that asked for nothing shorter than sixteen months did not ask for a four-month
   * deal, exactly as one that asked for nothing longer than forty did not ask for fifty-two.
   * Both are kept, and both stay changeable by hand.
   */
  for (const months of distinctMonths) {
    if (target.has(months)) continue;
    if (months > longest || months < shortest) target.set(months, { kind: 'keep' });
  }

  // 3 — the rest, in order, onto the years still free
  const freeYears = years.filter(y => !takenYears.has(y));
  let next = 0;
  for (const months of distinctMonths) {
    if (target.has(months)) continue;
    const year = freeYears[next];
    if (year === undefined) { target.set(months, { kind: 'keep' }); continue; }
    next += 1;
    target.set(months, { kind: 'year', year, months: normalised.get(year)! });
  }

  const terms: TermProposal[] = parsed.map(p => {
    const suggested = target.get(p.months) ?? { kind: 'keep' as const };
    return {
      fromKey: p.key,
      fromMonths: p.months,
      suffix: p.suffix,
      suggested,
      alreadyCorrect: suggested.kind === 'year' && suggested.months === p.months,
    };
  });

  return {
    terms,
    exactYears: exactYears.sort((a, b) => a - b),
    suggestSelected: terms.some(t => t.suggested.kind === 'year' && !t.alreadyCorrect),
  };
};

/**
 * The moves and removals a set of per-term targets implies.
 *
 * Targets come from the panel, where the reviewer may have changed any of them. Two DIFFERENT
 * lengths cannot be sent to the same year — the later one is dropped from the result rather than
 * overwriting the first, which is the same precedence applyOfferReterm already uses.
 *
 * ⚠️ TWO VARIANTS OF ONE LENGTH ARE NOT TWO TERMS.
 *
 * Energy Australia quotes `24` and `24_stepped` — one twenty-four-month product priced two ways.
 * Both belong on the same year, and a guard that claimed years per TERM dropped the second of
 * them: the smooth ladder moved and the stepped ladder stayed where it was, leaving the offer
 * priced over two different contract lengths at once. The claim is per (year, source length),
 * so variants travel together and a genuine clash is still refused.
 */
export const movesFromTargets = (
  terms: Array<{ fromKey: string; fromMonths: number; suffix: string; target: TermTarget }>,
): { moves: TermMove[]; removeKeys: string[] } => {
  const moves: TermMove[] = [];
  const removeKeys: string[] = [];
  const claimed = new Map<number, number>();   // year -> the source length that claimed it

  for (const t of terms ?? []) {
    if (!t || typeof t.fromKey !== 'string') continue;
    if (t.target?.kind === 'remove') { removeKeys.push(t.fromKey); continue; }
    if (t.target?.kind !== 'year') continue;
    const holder = claimed.get(t.target.year);
    if (holder !== undefined && holder !== t.fromMonths) continue;
    claimed.set(t.target.year, t.fromMonths);
    if (t.target.months === t.fromMonths) continue;          // already there
    moves.push({
      fromKey: t.fromKey,
      toKey: `${t.target.months}${t.suffix}`,
      fromMonths: t.fromMonths,
      toMonths: t.target.months,
    });
  }
  return { moves, removeKeys };
};

/** contract_terms with the given keys dropped. */
export const removeTerms = <T extends Record<string, any>>(contractTerms: T, keys: string[]): T => {
  if (!contractTerms || typeof contractTerms !== 'object' || !keys?.length) return contractTerms;
  const next: Record<string, any> = { ...contractTerms };
  for (const k of keys) delete next[k];
  return next as T;
};

export const isRetermPlan = (
  result: OfferRetermPlan | OfferRetermSkip,
): result is OfferRetermPlan => 'moves' in result;

/**
 * A contract_terms object with the planned moves applied.
 *
 * Deliberately identical in effect to the single-term editor's own re-term
 * (OfferReviewComparison's handleModifyTerm), so a bulk pass and nine manual edits produce the
 * same data:
 *
 *   - rate_type and every priced year slot are carried over verbatim; rates never change
 *   - getExpectedYearsForTerm prunes year slots the new, shorter term cannot hold, and never
 *     invents one the retailer did not quote
 *   - rebaseTermYearRanges rewrites the quoted dates onto the new month count, because a
 *     12-month range under a "4 month" key is thrown away by the engine's label-corroboration
 *     guard (15-day drift limit) and silently drops the term to the months-based approximation
 *     `months / 12 - 1/365` — which priced three re-termed retailers 0.28% under the client's
 *     model on prod tender bd3f75f1
 *
 * A move onto a key that already exists is skipped rather than overwriting it: the existing
 * term is the retailer's own quote for that length and outranks a remap of another one.
 */
export const applyOfferReterm = <T extends Record<string, any>>(
  contractTerms: T,
  moves: TermMove[],
): T => {
  if (!contractTerms || typeof contractTerms !== 'object' || moves.length === 0) {
    return contractTerms;
  }

  const next: Record<string, any> = { ...contractTerms };

  for (const move of moves) {
    const oldData = next[move.fromKey];
    if (!oldData || typeof oldData !== 'object') continue;
    if (move.toKey !== move.fromKey && next[move.toKey] !== undefined) continue;

    const years = getExpectedYearsForTerm(move.toMonths, oldData as Record<string, any>);
    const rebuilt: Record<string, any> = {};
    if (oldData.rate_type !== undefined) rebuilt.rate_type = oldData.rate_type;
    if (oldData.rateType !== undefined) rebuilt.rateType = oldData.rateType;
    years.forEach(y => { rebuilt[y] = oldData[y] ?? {}; });

    if (move.toKey !== move.fromKey) delete next[move.fromKey];
    next[move.toKey] = rebaseTermYearRanges(rebuilt, move.toMonths);
  }

  return next as T;
};

/** The contract_terms object on a parsed offer, whichever casing it uses. */
export const contractTermsOf = (parsedData: any): { key: string; terms: Record<string, any> } | null => {
  if (!parsedData || typeof parsedData !== 'object') return null;
  if (parsedData.contract_terms && typeof parsedData.contract_terms === 'object') {
    return { key: 'contract_terms', terms: parsedData.contract_terms };
  }
  if (parsedData.contractTerms && typeof parsedData.contractTerms === 'object') {
    return { key: 'contractTerms', terms: parsedData.contractTerms };
  }
  return null;
};

/** A parsed offer with the plan applied, or the same object when there is nothing to do. */
export const applyRetermToParsedData = <T>(parsedData: T, moves: TermMove[]): T => {
  const found = contractTermsOf(parsedData);
  if (!found || moves.length === 0) return parsedData;
  return {
    ...(parsedData as any),
    [found.key]: applyOfferReterm(found.terms, moves),
  };
};
